/**
 * I Ching Hexagram Calculator
 * Calculates personalized hexagrams based on birth data
 */

import { HEXAGRAMS } from './hexagrams.js';
import logger from '@/shared/utils/core/logger';


/**
 * Calculate day of year from a date
 * @param {Date} date - The date to calculate from
 * @returns {number} Day of year (1-366)
 */
function getDayOfYear(date) {
  const start = new Date(date.getFullYear(), 0, 0);
  const diff = date - start;
  const oneDay = 1000 * 60 * 60 * 24;
  return Math.floor(diff / oneDay);
}

/**
 * Calculate a hexagram number from a date and optional time
 * Uses day of year and time modulo 64 to map to hexagrams 1-64
 *
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @param {string} [birthTime] - Optional birth time in HH:MM format
 * @param {number} [offset=0] - Offset for calculating different hexagrams
 * @returns {number} Hexagram number (1-64)
 */
function calculateHexagramNumber(birthDate, birthTime = null, offset = 0) {
  try {
    // Parse the date
    const dateTimeString = birthTime
      ? `${birthDate}T${birthTime}:00`
      : `${birthDate}T12:00:00`; // Default to noon if no time provided

    const date = new Date(dateTimeString);

    if (isNaN(date.getTime())) {
      logger.warn('Invalid date provided to calculateHexagramNumber:', birthDate, birthTime);
      return 1; // Default to hexagram 1
    }

    // Get day of year (1-366)
    const dayOfYear = getDayOfYear(date);

    // Add time component if provided (0-1440 minutes in a day)
    let timeComponent = 0;
    if (birthTime) {
      const [hours, minutes] = birthTime.split(':').map(Number);
      timeComponent = hours * 60 + minutes;
    }

    // Combine day and time with offset to get a number
    // Use prime numbers to ensure good distribution across all 64 hexagrams
    const combined = (dayOfYear * 97 + timeComponent * 13 + offset * 73) % 64;

    // Return hexagram number (1-64)
    return combined === 0 ? 64 : combined;
  } catch (error) {
    logger.error('Error calculating hexagram number:', error);
    return 1; // Default to hexagram 1 on error
  }
}

/**
 * Calculate complete hexagram profile for a person
 * Returns 4 key hexagrams based on birth data
 *
 * @param {string} birthDate - Birth date in YYYY-MM-DD format
 * @param {string} [birthTime] - Optional birth time in HH:MM format
 * @param {string} [birthPlace] - Optional birth place (not used in basic calculation)
 * @returns {Object} Profile with personality, design, life_purpose, and relationships hexagrams
 */
export function calculateHexagramProfile(birthDate, birthTime = null, birthPlace = null) {
  if (!birthDate) {
    logger.warn('No birth date provided for hexagram calculation');
    return null;
  }

  // Calculate the 4 key hexagrams using different offsets
  const personality = calculateHexagramNumber(birthDate, birthTime, 0);
  const design = calculateHexagramNumber(birthDate, birthTime, 88); // ~88 days before birth (design date)
  const lifePurpose = calculateHexagramNumber(birthDate, birthTime, 180); // Opposite side of year
  const relationships = calculateHexagramNumber(birthDate, birthTime, 270); // 3/4 year offset

  return {
    personality,
    design,
    life_purpose: lifePurpose,
    relationships,
    calculatedAt: new Date().toISOString(),
    birthData: {
      date: birthDate,
      time: birthTime || null,
      place: birthPlace || null
    }
  };
}

/**
 * Get hexagram details by number
 * @param {number} hexagramNumber - Number 1-64
 * @returns {Object|null} Hexagram details or null if not found
 */
export function getHexagramDetails(hexagramNumber) {
  if (!hexagramNumber || hexagramNumber < 1 || hexagramNumber > 64) {
    logger.warn('Invalid hexagram number:', hexagramNumber);
    return null;
  }

  return HEXAGRAMS[hexagramNumber] || null;
}

/**
 * Get a daily hexagram based on current date and user's birth data
 * This provides a "daily reading" that rotates through hexagrams
 *
 * @param {string} birthDate - User's birth date
 * @param {string} [birthTime] - User's birth time
 * @returns {Object} Today's hexagram with details
 */
export function getDailyHexagram(birthDate, birthTime = null) {
  // Use today's date combined with birth data for daily variation
  const today = new Date();
  const todayString = today.toISOString().split('T')[0];

  // Calculate using today's date offset
  const dayOffset = getDayOfYear(today);
  const hexagramNumber = calculateHexagramNumber(birthDate, birthTime, dayOffset);

  return {
    number: hexagramNumber,
    details: getHexagramDetails(hexagramNumber),
    date: todayString
  };
}

/**
 * Get personalized journal prompt based on hexagram
 * Uses the hexagram's themes and AI guidance to provide contextual prompting
 *
 * @param {number} hexagramNumber - The hexagram number
 * @param {string} [context='general'] - Context for the prompt (general, shadow, gift, essence)
 * @returns {string} Personalized journal prompt
 */
export function getJournalPrompt(hexagramNumber, context = 'general') {
  const hexagram = getHexagramDetails(hexagramNumber);

  if (!hexagram) {
    return "What's on your mind today? Let your thoughts flow freely...";
  }

  // Generate prompts based on context
  const prompts = {
    general: [
      `Reflect on ${hexagram.name.toLowerCase()} in your life. ${hexagram.themes[0]} has been calling your attention. What comes up for you?`,
      `Today's energy is about ${hexagram.themes[0]}. How does this show up in your life right now?`,
      `Consider the theme of ${hexagram.name.toLowerCase()}. What insights arise for you?`
    ],
    shadow: [
      `The shadow of ${hexagram.name.toLowerCase()} is ${hexagram.shadow}. Where might you be experiencing this?`,
      `Notice any places where you're facing ${hexagram.themes[0]} challenges. What do you need to see or understand?`,
      `Gently explore: ${hexagram.shadow}. What might this be showing you?`
    ],
    gift: [
      `Your gift related to ${hexagram.name.toLowerCase()} is ${hexagram.gift}. How are you expressing this lately?`,
      `Celebrate your capacity for ${hexagram.themes[0]}. Where do you see this strength showing up?`,
      `${hexagram.gift} - this is available to you. How might you embody this more fully?`
    ],
    essence: [
      `At the essence level, ${hexagram.name.toLowerCase()} invites: ${hexagram.essence}`,
      `What if ${hexagram.essence.toLowerCase()}? How does this land for you?`,
      `Contemplate this deeper truth: ${hexagram.essence}`
    ]
  };

  const promptList = prompts[context] || prompts.general;
  const randomIndex = Math.floor(Math.random() * promptList.length);

  return promptList[randomIndex];
}

/**
 * Get AI guidance for journal feedback based on hexagram and journal keywords
 * This can be used to customize AI responses based on the user's hexagram profile
 *
 * @param {Object} hexagramProfile - User's hexagram profile
 * @param {string} journalContent - Content of journal entry
 * @returns {Object} AI guidance suggestions
 */
export function getAIGuidanceContext(hexagramProfile, journalContent = '') {
  if (!hexagramProfile || !hexagramProfile.personality) {
    return null;
  }

  const personalityHex = getHexagramDetails(hexagramProfile.personality);

  if (!personalityHex) {
    return null;
  }

  // Check if journal content contains keywords from this hexagram
  const lowerContent = journalContent.toLowerCase();
  const matchingKeywords = personalityHex.journal_keywords.filter(
    keyword => lowerContent.includes(keyword)
  );

  return {
    hexagram: personalityHex,
    matchingKeywords,
    guidance: personalityHex.ai_guidance,
    suggestedThemes: personalityHex.themes,
    // Determine which aspect (shadow/gift/essence) seems most relevant
    likelyAspect: matchingKeywords.length > 0 ? 'shadow' : 'gift'
  };
}

/**
 * Calculate hexagram compatibility/resonance between two people
 * Useful for relationship insights
 *
 * NOTE: Currently unused - reserved for future relationship insights feature
 *
 * @param {Object} profile1 - First person's hexagram profile
 * @param {Object} profile2 - Second person's hexagram profile
 * @returns {Object} Compatibility insights
 */
export function calculateCompatibility(profile1, profile2) {
  if (!profile1 || !profile2) {
    return null;
  }

  const hex1 = getHexagramDetails(profile1.personality);
  const hex2 = getHexagramDetails(profile2.personality);

  if (!hex1 || !hex2) {
    return null;
  }

  // Find overlapping themes
  const commonThemes = hex1.themes.filter(theme =>
    hex2.themes.includes(theme)
  );

  // Calculate simple compatibility score (0-100)
  const score = (commonThemes.length / Math.max(hex1.themes.length, hex2.themes.length)) * 100;

  return {
    score: Math.round(score),
    commonThemes,
    hex1Summary: {
      number: hex1.number,
      name: hex1.name,
      themes: hex1.themes
    },
    hex2Summary: {
      number: hex2.number,
      name: hex2.name,
      themes: hex2.themes
    },
    insight: score > 50
      ? `High resonance around: ${commonThemes.join(', ')}`
      : 'Complementary energies - differences create growth opportunities'
  };
}

export default {
  calculateHexagramProfile,
  getHexagramDetails,
  getDailyHexagram,
  getJournalPrompt,
  getAIGuidanceContext,
  calculateCompatibility
};
